import { gradeLesson, type GradeLessonOpts } from "./engine";
import type { BlockRegistry } from "./registry";
import type { AuthoredBlock, BlockResponse, LessonResult } from "./types";

/**
 * Antifraude de progreso. Regla de oro: la nota que cuenta es la que
 * RECALCULA el servidor con el registro autoritativo — nunca la que envía el
 * cliente. Este módulo re-corrige y detecta señales de manipulación.
 */

export interface AttemptSubmission {
  lessonId: string;
  /** Nota que el cliente AFIRMA (se ignora salvo para detectar discrepancia). */
  claimedScore?: number;
  responses: BlockResponse[];
  /** Momento de inicio y fin del intento (epoch ms). */
  startedAt?: number;
  finishedAt?: number;
}

export interface AntifraudOpts extends GradeLessonOpts {
  /** ms mínimos plausibles por bloque respondido (default 400). */
  minMsPerBlock?: number;
  /** Tolerancia de discrepancia cliente/servidor antes de marcar (default 0.001). */
  scoreTolerance?: number;
  /** IDs de intento ya vistos (anti-replay). */
  seenAttemptIds?: ReadonlySet<string>;
  /** ID de este intento (para anti-replay). */
  attemptId?: string;
}

export type FraudFlag =
  | "score-mismatch" // el cliente afirmó una nota distinta a la recalculada
  | "too-fast" // respondió por debajo del mínimo plausible
  | "duplicate-response" // dos respuestas para el mismo bloque
  | "unknown-block" // respuesta a un bloque que no existe en la lección
  | "replay"; // intento ya procesado

export interface AntifraudResult {
  /** Nota AUTORITATIVA recalculada en servidor. Es la única que se persiste. */
  result: LessonResult;
  flags: FraudFlag[];
  /** ¿Aceptar el intento? (flags graves ⇒ false). */
  accepted: boolean;
}

const GRAVE: ReadonlySet<FraudFlag> = new Set<FraudFlag>(["replay"]);

/**
 * Verifica y RE-CORRIGE un intento. Determinista; ejecutable en el servidor.
 */
export function verifyAttempt(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  submission: AttemptSubmission,
  opts: AntifraudOpts = {},
): AntifraudResult {
  const flags: FraudFlag[] = [];
  const minMs = opts.minMsPerBlock ?? 400;
  const tol = opts.scoreTolerance ?? 0.001;

  // Anti-replay.
  if (opts.attemptId && opts.seenAttemptIds?.has(opts.attemptId)) {
    flags.push("replay");
  }

  // Respuestas duplicadas / a bloques inexistentes.
  const blockIds = new Set(blocks.map((b) => b.id));
  const seenResponses = new Set<string>();
  for (const r of submission.responses) {
    if (!blockIds.has(r.blockId)) {
      flags.push("unknown-block");
    }
    if (seenResponses.has(r.blockId)) {
      flags.push("duplicate-response");
    }
    seenResponses.add(r.blockId);
    if (typeof r.elapsedMs === "number" && r.elapsedMs >= 0 && r.elapsedMs < minMs) {
      if (!flags.includes("too-fast")) {
        flags.push("too-fast");
      }
    }
  }

  // Timing global: si el intento total es más rápido que minMs × nº de bloques.
  if (
    submission.startedAt != null &&
    submission.finishedAt != null &&
    submission.finishedAt > submission.startedAt
  ) {
    const elapsed = submission.finishedAt - submission.startedAt;
    if (elapsed < minMs * blocks.length && !flags.includes("too-fast")) {
      flags.push("too-fast");
    }
  }

  // Recorrección autoritativa.
  const result = gradeLesson(registry, blocks, submission.responses, opts);

  // Discrepancia con lo que afirmó el cliente.
  if (submission.claimedScore != null && Math.abs(submission.claimedScore - result.score) > tol) {
    flags.push("score-mismatch");
  }

  const accepted = !flags.some((f) => GRAVE.has(f));
  return { result, flags, accepted };
}
